import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Avatar,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import { MasterListData } from "../../types/List";
import { imageBaseUrl } from "../../api/Index";
import { initialUserData, UserData } from "../../types/User";
import { PatientData } from "../../types/Patient";
import { masterListEnum } from "../../utils/AppEnum";
import { EmployeeData, initialEmployeeData } from "../../types/Employee";
import { ConsultingRoomData } from "../../types/ConsultingRoom";

interface ComponentProps {
  initialData?: Partial<ConsultingRoomData>;
  onSubmit: (user: Partial<ConsultingRoomData>) => void;
  onClose: () => void;
}

const ConsultingRoomForm: React.FC<ComponentProps> = ({
  initialData,
  onSubmit,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [form, setForm] = useState<Partial<ConsultingRoomData>>(
    initialData || initialUserData
  );

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setForm((prev: Partial<PatientData>) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  function fileToBase64(file: File): Promise<{ base64: string; mime: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => {
        reader.abort();
        reject(new DOMException("Problem reading file."));
      };
      reader.onload = () => {
        const result = reader.result as string; // data:[mime];base64,AAAA...
        // optionally strip data URL prefix:
        const match = result.match(/^data:(.+);base64,(.*)$/);
        if (!match) return reject(new Error("Invalid file data"));
        resolve({ mime: match[1], base64: match[2] });
      };
      reader.readAsDataURL(file);
    });
  }

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.name) newErrors.name = "Name is required";
    if (!form.size) newErrors.size = "The size is required";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
      {/* 🔹 Responsive grid layout */}
      <Box
        display="grid"
        gridTemplateColumns="1fr" // always one column
        gap={2}
      >
        <Box>
          <TextField
            name="name"
            label="Room Name"
            value={form.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            name="size"
            label="Size"
            type="number"
            value={form.size}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.size}
            helperText={errors.size}
            InputProps={{
              inputProps: {
                min: 1,
                max: 10,
              },
            }}
          />

          <TextField
            name="description"
            label="Description"
            value={form.description}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
        </Box>
      </Box>

      {/* Action buttons */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        marginTop={3}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth
        >
          Close
        </Button>
        <Button type="submit" variant="contained" color="success" fullWidth>
          Save
        </Button>
      </Box>
    </form>
  );
};

export default ConsultingRoomForm;
